import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAuthPolicy } from '../../../services/auth/policy.service';
import { ApiDigitalAuthResource } from '../../../services/auth/resource.service';
import { Policy, Statement, ResourceCatalog } from '../../../models/policy.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import StatementBuilder from '../../../components/StatementBuilder';

import AppPreloader from '../../../components/Preloader';

export interface PolicyViewPageProps {};

const PolicyViewPage: React.FunctionComponent<PolicyViewPageProps> = props => {
  const {setRoute, appServiceForUser} = UseOutletContextAdmin()
  const {lang} = StateLanguage()
  const {id} = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [policy, setPolicy] = useState<Policy | null>(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editStatements, setEditStatements] = useState<Statement[]>([])
  const [saving, setSaving] = useState(false)
  const [catalog, setCatalog] = useState<ResourceCatalog | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(true)

  const loadCatalog = async (api_key: string) => {
    setCatalogLoading(true)
    try {
      const response = await ApiDigitalAuthResource.getCatalog(api_key)
      if (response.status === 200 && response.data) {
        setCatalog(response.data)
      }
    } catch {
      // catalog load failure is non-blocking
    } finally {
      setCatalogLoading(false)
    }
  }

  const loadPolicy = async (api_key: string) => {
    if (!id) return
    await ApiDigitalAuthPolicy.getPolicy(api_key, id).then((response) => {
      if (response.status === 200 && response.data) {
        const p = response.data
        setPolicy(p)
        setEditName(p.name ?? '')
        setEditDescription(p.description ?? '')
        setEditStatements(p.statements ?? [])
      } else {
        errorWebHandler(lang, {response})
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const isValid = (): boolean => {
    if (!editName.trim()) return false
    if (editStatements.length === 0) return false
    return editStatements.every(s => s.actions.length > 0 && s.resources.length > 0)
  }

  const handleUpdate = async () => {
    if (!appServiceForUser || !id || !isValid()) return
    setSaving(true)

    await ApiDigitalAuthPolicy.updatePolicy(appServiceForUser.api_key, id, {
      name: editName.trim(),
      description: editDescription.trim(),
      statements: editStatements
    }).then((response) => {
      if (response.status === 200) {
        loadPolicy(appServiceForUser!.api_key)
      } else {
        GlobalSwal.fire({
          title: response.data?.message ?? lang.labels.error,
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setSaving(false)
    })
  }

  const handleDelete = async () => {
    if (!appServiceForUser || !id) return

    GlobalSwal.fire({
      title: lang.labels.confirmDeletePolicy,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ApiDigitalAuthPolicy.deletePolicy(appServiceForUser!.api_key, id!).then((response) => {
          if (response.status === 200) {
            navigate('/main/admin/policies')
          } else {
            GlobalSwal.fire({
              title: response.data?.message ?? lang.labels.error,
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch((error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  useEffect(() => {
    if (appServiceForUser) {
      loadPolicy(appServiceForUser.api_key)
      loadCatalog(appServiceForUser.api_key)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appServiceForUser])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.policies}, company: true})

    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { policy
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h3 className="fw-bolder">
                  {policy.name}
                  {policy.is_system && <span className="badge badge-light-info fs-8 ms-2">{lang.labels.system}</span>}
                </h3>
              </div>
              { !policy.is_system &&
                <div className="card-toolbar">
                  <button className="btn btn-sm btn-light-danger" type="button" onClick={handleDelete}>{lang.labels.delete}</button>
                </div>
              }
            </div>
            <div className="card-body pt-0">
              { !policy.is_system
                ?
                <>
                  <div className="mb-5">
                    <label className="form-label fw-bolder">{lang.labels.name} *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="form-label fw-bolder">{lang.labels.description}</label>
                    <textarea
                      className="form-control"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      rows={3}
                      maxLength={500}
                    />
                  </div>
                </>
                :
                <div className="text-muted mb-5">{lang.labels.systemReadOnly}</div>
              }
            </div>
          </div>
          <div className="card card-flush mb-5">
            <div className="card-body">
              <StatementBuilder
                statements={policy.is_system ? (policy.statements ?? []) : editStatements}
                catalog={catalog}
                catalogLoading={catalogLoading}
                readOnly={policy.is_system}
                onChange={setEditStatements}
              />
            </div>
          </div>
          { !policy.is_system &&
            <div className="d-flex justify-content-end">
              <button className="btn btn-primary" type="button" onClick={handleUpdate} disabled={saving || !isValid()}>
                {saving ? lang.labels.loading : lang.labels.save}
              </button>
            </div>
          }
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default PolicyViewPage;
